import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, Form, Button, Row, Col, Alert, Spinner, Modal, Tab, Nav } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { ClientSelectGroup } from '@/components/ClientSelectGroup';
import { ClientConfigForm } from '@/components/config/ClientConfigForm';
import { ConfigHygienePanel } from '@/components/config/ConfigHygienePanel';
import { JsonDiffView } from '@/components/config/JsonDiffView';
import { QuotaCheckCard } from '@/components/tools/QuotaCheckCard';
import { clientService } from '@/services/clientService';
import { clientMetadataService } from '@/services/clientMetadataService';
import { Client, ClientConfig, clientConfigSchema, CLIENT_STATUS_VALUES, CLIENT_STATUS_DISPLAY } from '@/types';
import type { ClientStatusValue } from '@/types';
import { FileExportService } from '@/utils/fileExport';

export default function UpdateClientConfig() {
  const [searchParams] = useSearchParams();

  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClientName, setSelectedClientName] = useState('');
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  // Unsaved form edits — hygiene-panel removal re-hydrates the form, so it is
  // blocked while true to avoid silently discarding the operator's changes.
  const [formDirty, setFormDirty] = useState(false);
  // Bumped on every successful save so the form shows its green "Saved" note.
  const [saveCounter, setSaveCounter] = useState(0);

  // Pending diff from the shared form, shown in the confirm modal
  const [pendingUpdates, setPendingUpdates] = useState<Partial<ClientConfig> | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [beforeJson, setBeforeJson] = useState('');
  const [afterJson, setAfterJson] = useState('');

  // Client metadata (non-deployment)
  const [metaStatus, setMetaStatus] = useState<ClientStatusValue>('unclear');
  const [trialStartDate, setTrialStartDate] = useState('');
  const [trialEndDate, setTrialEndDate] = useState('');
  const [metaNotes, setMetaNotes] = useState('');

  // Developer JSON replace
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [uploadedConfig, setUploadedConfig] = useState<ClientConfig | null>(null);
  const [showReplacePreview, setShowReplacePreview] = useState(false);

  // Prefill selected client from query params (if present)
  useEffect(() => {
    const qClient = (searchParams.get('clientName') || '').trim();
    if (qClient) setSelectedClientName(qClient);
  }, []);

  const loadClients = async () => {
    try {
      setWorking(true);
      const list = await clientService.getAllClients();
      setClients(list);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load clients');
    } finally {
      setWorking(false);
    }
  };

  useEffect(() => {
    loadClients();
  }, []);

  // Load metadata for the selected client
  useEffect(() => {
    if (!selectedClientName) return;
    clientMetadataService.getMetadata(selectedClientName).then((meta) => {
      if (meta) {
        setMetaStatus(meta.status ?? 'unclear');
        setTrialStartDate(meta.trialStartDate || '');
        setTrialEndDate(meta.trialEndDate || '');
        setMetaNotes(meta.notes || '');
      } else {
        setMetaStatus('unclear');
        setTrialStartDate('');
        setTrialEndDate('');
        setMetaNotes('');
      }
    });
  }, [selectedClientName]);

  const selectedClient = clients.find((c) => c.name === selectedClientName);

  const handleFormSubmit = (updates: Partial<ClientConfig>) => {
    setError(null);
    setSuccess(null);
    if (!selectedClientName) {
      setError('Select a client');
      return;
    }
    setPendingUpdates(updates);
    setShowPreview(true);
  };

  /**
   * Apply a saved diff to the locally-held client list. DynamoDB scans are
   * eventually consistent, so refetching immediately after a write can return
   * the OLD record — the form would rehydrate stale and keep showing pending
   * changes. Patching locally from what we just wrote is deterministic; the
   * service cache was already busted so other pages refetch fresh later.
   * `undefined` values mean the key was REMOVEd.
   */
  const patchSelectedClientConfig = (updates: Partial<ClientConfig>) => {
    setClients((prev) =>
      prev.map((c) => {
        if (c.name !== selectedClientName) return c;
        const cfg: Record<string, unknown> = { ...c.config };
        for (const [key, value] of Object.entries(updates)) {
          if (value === undefined) delete cfg[key];
          else cfg[key] = value;
        }
        return { ...c, config: cfg as ClientConfig };
      })
    );
  };

  const saveMetadataQuietly = async () => {
    try {
      await clientMetadataService.saveMetadata({
        clientName: selectedClientName,
        status: metaStatus,
        ...(trialStartDate ? { trialStartDate } : {}),
        ...(trialEndDate ? { trialEndDate } : {}),
        ...(metaNotes ? { notes: metaNotes } : {}),
      });
    } catch (metaErr) {
      console.error('Failed to save metadata alongside config update:', metaErr);
    }
  };

  return (
    <div>
      {/* Client Selection - always visible above tabs */}
      <Card className="border-0 shadow-sm mb-3">
        <Card.Header>
          <h5 className="mb-0">Update Client Config</h5>
          <p className="text-muted small mb-0 mt-2">
            Modify an existing client configuration. Values that differ from defaults are highlighted; use the rail to
            search settings and jump between sections.
          </p>
        </Card.Header>
        <Card.Body>
          <Form.Group>
            <Form.Label className="fw-semibold">Select Client</Form.Label>
            <ClientSelectGroup value={selectedClientName} onChange={setSelectedClientName} clients={clients} />
            {selectedClient && (
              <Form.Text className="text-muted">
                Account: {selectedClient.config.clientAccountId} | Type:{' '}
                {selectedClient.config.devInstance ? 'Development' : 'Production'}
              </Form.Text>
            )}
          </Form.Group>
        </Card.Body>
      </Card>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {selectedClientName && selectedClient && (
        <Tab.Container defaultActiveKey="config">
          <Nav variant="tabs" className="mb-3">
            <Nav.Item>
              <Nav.Link eventKey="config">Deployment Configuration</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="metadata">Client Metadata</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="quotas">Quota Check</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="json">JSON (Developers)</Nav.Link>
            </Nav.Item>
          </Nav>

          <Tab.Content>
            {/* Tab 1: Deployment Configuration */}
            <Tab.Pane eventKey="config">
              <ConfigHygienePanel
                clientName={selectedClientName}
                config={selectedClient.config}
                disabled={working || formDirty}
                disabledNote={formDirty ? 'Save or discard your pending form changes before removing keys.' : undefined}
                onCleaned={(keys) =>
                  patchSelectedClientConfig(
                    Object.fromEntries(keys.map((k) => [k, undefined])) as Partial<ClientConfig>
                  )
                }
                onError={setError}
                onSuccess={setSuccess}
              />

              <ClientConfigForm
                mode="update"
                currentConfig={selectedClient.config}
                working={working}
                submitLabel="Save Changes"
                submitHint="Modified values and enabled on-by-default flags are written"
                onSubmit={handleFormSubmit}
                onError={setError}
                onDirtyChange={setFormDirty}
                saveCounter={saveCounter}
              />
            </Tab.Pane>

            {/* Tab 2: Client Metadata */}
            <Tab.Pane eventKey="metadata">
              <Card className="border-0 shadow-sm">
                <Card.Header>
                  <h6 className="mb-0">Client Metadata (Non-Deployment)</h6>
                  <p className="text-muted small mb-0 mt-2">
                    Track trial status, dates, and notes. This data is stored separately from the deployment config and
                    does not affect infrastructure.
                  </p>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold">Status</Form.Label>
                        <Form.Select
                          value={metaStatus}
                          onChange={(e) => setMetaStatus(e.target.value as ClientStatusValue)}
                        >
                          {CLIENT_STATUS_VALUES.map((s) => (
                            <option key={s} value={s}>
                              {CLIENT_STATUS_DISPLAY[s].label}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label className={metaStatus !== 'trial' ? 'text-muted' : ''}>Trial Start Date</Form.Label>
                        <DatePicker
                          selected={trialStartDate ? new Date(trialStartDate) : null}
                          onChange={(date: Date | null) =>
                            setTrialStartDate(date ? date.toISOString().split('T')[0] : '')
                          }
                          dateFormat="yyyy-MM-dd"
                          className="form-control"
                          placeholderText="Select start date"
                          disabled={metaStatus !== 'trial'}
                          showMonthDropdown
                          showYearDropdown
                          dropdownMode="select"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label className={metaStatus !== 'trial' ? 'text-muted' : ''}>Trial End Date</Form.Label>
                        <DatePicker
                          selected={trialEndDate ? new Date(trialEndDate) : null}
                          onChange={(date: Date | null) =>
                            setTrialEndDate(date ? date.toISOString().split('T')[0] : '')
                          }
                          dateFormat="yyyy-MM-dd"
                          className="form-control"
                          placeholderText="Select end date"
                          disabled={metaStatus !== 'trial'}
                          showMonthDropdown
                          showYearDropdown
                          dropdownMode="select"
                          minDate={trialStartDate ? new Date(trialStartDate) : undefined}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Form.Group className="mb-3">
                    <Form.Label>Notes</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={metaNotes}
                      onChange={(e) => setMetaNotes(e.target.value)}
                      placeholder="Free text notes about this client..."
                    />
                  </Form.Group>
                  <Button
                    variant="outline-primary"
                    disabled={working}
                    onClick={async () => {
                      try {
                        setWorking(true);
                        await clientMetadataService.saveMetadata({
                          clientName: selectedClientName,
                          status: metaStatus,
                          ...(trialStartDate ? { trialStartDate } : {}),
                          ...(trialEndDate ? { trialEndDate } : {}),
                          ...(metaNotes ? { notes: metaNotes } : {}),
                        });
                        setSuccess('Metadata saved');
                      } catch (err) {
                        setError(err instanceof Error ? err.message : 'Failed to save metadata');
                      } finally {
                        setWorking(false);
                      }
                    }}
                  >
                    {working ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Saving...
                      </>
                    ) : (
                      'Save Metadata'
                    )}
                  </Button>
                </Card.Body>
              </Card>
            </Tab.Pane>

            {/* Tab 3: Quota Check */}
            <Tab.Pane eventKey="quotas">
              <QuotaCheckCard
                accountId={selectedClient.config.clientAccountId}
                region={selectedClient.config.region || 'us-east-1'}
                disabled={working}
              />
            </Tab.Pane>

            {/* Tab 4: Developer JSON Replace */}
            <Tab.Pane eventKey="json">
              {/* Developer JSON Replace */}
              <Card className="border-0 shadow-sm mt-4">
                <Card.Header>
                  <h6 className="mb-0">Replace Config from JSON (Developers)</h6>
                  <p className="text-muted small mb-0 mt-2">
                    Upload a full JSON config to replace the current configuration. Validation is strict. This mirrors
                    the CLI write-config replace behavior.
                  </p>
                </Card.Header>
                <Card.Body>
                  <Row className="align-items-end g-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-semibold">Upload JSON file</Form.Label>
                        <Form.Control
                          type="file"
                          accept="application/json,.json"
                          onChange={async (e) => {
                            setJsonError(null);
                            setUploadedConfig(null);
                            const file = e.currentTarget.files?.[0];
                            if (!file) return;
                            try {
                              const text = await file.text();
                              const parsed = JSON.parse(text);
                              const validated = clientConfigSchema.strict().parse(parsed);
                              setUploadedConfig(validated as ClientConfig);
                            } catch (err: any) {
                              const msg = err?.message || 'Invalid JSON or schema mismatch';
                              setJsonError(msg);
                            }
                          }}
                        />
                        <Form.Text className="text-muted">
                          Only valid JSON matching the strict schema is accepted.
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={6} className="d-flex gap-2">
                      <Button
                        variant="outline-secondary"
                        disabled={!selectedClient}
                        onClick={() => {
                          if (!selectedClient) return;
                          const content = JSON.stringify(selectedClient.config, null, 2);
                          FileExportService.downloadFile({
                            name: `${selectedClient.name}.json`,
                            content,
                            mimeType: 'application/json',
                            size: new Blob([content]).size,
                          });
                        }}
                      >
                        Download current JSON
                      </Button>
                      <Button
                        variant="primary"
                        disabled={!uploadedConfig || !selectedClientName || working}
                        onClick={() => {
                          try {
                            setJsonError(null);
                            const before = clients.find((c) => c.name === selectedClientName)?.config;
                            setBeforeJson(JSON.stringify(before, null, 2));
                            setAfterJson(JSON.stringify(uploadedConfig, null, 2));
                            setShowReplacePreview(true);
                          } catch (err: any) {
                            setJsonError(err?.message || 'Unable to prepare preview');
                          }
                        }}
                      >
                        Preview Replace
                      </Button>
                    </Col>
                  </Row>
                  {jsonError && (
                    <Alert variant="danger" className="mt-3">
                      {jsonError}
                    </Alert>
                  )}
                </Card.Body>
              </Card>
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      )}

      <Modal show={showPreview} onHide={() => setShowPreview(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Update</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <JsonDiffView
            before={selectedClient?.config as Record<string, unknown> | undefined}
            after={
              pendingUpdates
                ? { ...((selectedClient?.config as Record<string, unknown>) || {}), ...pendingUpdates }
                : undefined
            }
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPreview(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={working || !pendingUpdates}
            onClick={async () => {
              if (!pendingUpdates) return;
              try {
                setWorking(true);
                await clientService.updateClientConfig(selectedClientName, pendingUpdates);
                await saveMetadataQuietly();
                patchSelectedClientConfig(pendingUpdates);
                setSaveCounter((c) => c + 1);
                setSuccess('Configuration updated');
                setShowPreview(false);
                setPendingUpdates(null);
              } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to update config');
              } finally {
                setWorking(false);
              }
            }}
          >
            {working ? (
              <>
                <Spinner size="sm" className="me-2" />
                Saving…
              </>
            ) : (
              'Confirm Update'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showReplacePreview} onHide={() => setShowReplacePreview(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Replace</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted">
            This will replace the entire configuration for <strong>{selectedClientName || '—'}</strong>.
          </p>
          <Row>
            <Col md={6}>
              <h6 className="text-muted">Current</h6>
              <pre className="bg-light p-3 rounded" style={{ maxHeight: '50vh', overflow: 'auto' }}>
                {beforeJson}
              </pre>
            </Col>
            <Col md={6}>
              <h6 className="text-muted">New</h6>
              <pre className="bg-light p-3 rounded" style={{ maxHeight: '50vh', overflow: 'auto' }}>
                {afterJson}
              </pre>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowReplacePreview(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={working || !uploadedConfig || !selectedClientName}
            onClick={async () => {
              if (!uploadedConfig || !selectedClientName) return;
              try {
                setWorking(true);
                await clientService.replaceClientConfig(selectedClientName, uploadedConfig);
                // Full put — swap the whole local config for the uploaded one
                setClients((prev) =>
                  prev.map((c) => (c.name === selectedClientName ? { ...c, config: uploadedConfig } : c))
                );
                setSaveCounter((c) => c + 1);
                setSuccess('Configuration replaced');
                setShowReplacePreview(false);
                setUploadedConfig(null);
              } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to replace config');
              } finally {
                setWorking(false);
              }
            }}
          >
            {working ? (
              <>
                <Spinner size="sm" className="me-2" />
                Replacing…
              </>
            ) : (
              'Confirm Replace'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
